<template>
  <div
    class="tir-pm-dropdown"
    :class="{
      'tir-pm-dropdown--selected': isSelectedContent,
      'tir-pm-dropdown--opened': isOpened,
      'tir-pm-dropdown--with-hint': props.hintMessage,
      'tir-pm-dropdown--error': isWithError,
      'tir-pm-dropdown--disabled': isDisabled,
      'tir-pm-dropdown--regular': size === TirPmDropdownSizeEnum.Regular,
      'tir-pm-dropdown--light': size === TirPmDropdownSizeEnum.Light,
    }"
  >
    <TirPmPopover
      ref="$popover"
      :is-disabled="isDisabledPopover || isDisabled"
      :position="typeof popoverPosition === 'string' ? popoverPosition : undefined"
      :custom-position="typeof popoverPosition === 'object' ? popoverPosition : undefined"
      :width="popoverWidth"
      :classes="popoverClasses"
      @open="onOpenPopover"
      @close="onClosePopover"
    >
      <slot name="target" :is-opened="isOpened">
        <div class="tir-pm-dropdown__target">
          <span class="tir-pm-dropdown__label light" v-if="size === TirPmDropdownSizeEnum.Light">
            {{ label }}</span
          >
          <div class="tir-pm-dropdown__area-block">
            <div
              :class="[
                'tir-pm-dropdown__area',
                {
                  'tir-pm-dropdown__area-variant--detached':
                    variant === TirPmDropdownVariantEnum.Detached,
                },
                {
                  'tir-pm-dropdown--light': size === TirPmDropdownSizeEnum.Light,
                },
              ]"
              :style="{ width }"
            >
              <div class="tir-pm-dropdown__area-left-slot" v-if="isLeftSlot">
                <slot name="left" />
              </div>
              <div
                :class="[
                  'tir-pm-dropdown__desc-select',
                  {
                    'tir-pm-dropdown--light': size === TirPmDropdownSizeEnum.Light,
                  },
                ]"
              >
                <span class="tir-pm-dropdown__label" v-if="size !== TirPmDropdownSizeEnum.Light">{{
                  label
                }}</span>
                <div
                  class="tir-pm-dropdown__selected-item"
                  :class="{
                    'tir-pm-dropdown__selected-item--show':
                      isOpened || isSelectedContent || size === TirPmDropdownSizeEnum.Light,
                  }"
                >
                  <slot name="selectedContent">
                    <span class="tir-pm-dropdown__placeholder">
                      {{ placeholder }}
                    </span>
                  </slot>
                </div>
              </div>
              <div class="tir-pm-dropdown__area-right-slot" v-if="isRightSlot">
                <slot name="right" />
              </div>
              <div
                v-if="!isDisabled && variant === TirPmDropdownVariantEnum.Contained"
                :class="[
                  'tir-pm-dropdown__icon-open',
                  {
                    'tir-pm-dropdown--light': size === TirPmDropdownSizeEnum.Light,
                  },
                ]"
              >
                <ChevronDownIcon />
              </div>
            </div>
            <div
              v-if="!isDisabled && variant === TirPmDropdownVariantEnum.Detached"
              :class="[
                'tir-pm-dropdown__icon-open',
                {
                  'tir-pm-dropdown__icon-open-variant--detached':
                    variant === TirPmDropdownVariantEnum.Detached,
                },
                {
                  'tir-pm-dropdown--light': size === TirPmDropdownSizeEnum.Light,
                },
              ]"
            >
              <ChevronDownIcon />
            </div>
          </div>
        </div>
      </slot>
      <template #content>
        <div class="tir-pm-dropdown__popover-content">
          <div
            :class="[
              'tir-pm-dropdown__search-input-section',
              { 'is-empty-list': !viewOptions.length },
            ]"
            v-if="isEnabledSearch"
          >
            <TirPmInput
              class="tir-pm-dropdown__search-input"
              :placeholder="searchInputPlaceholder"
              :label="searchInputLabel"
              :is-with-hint="false"
              is-small-size
              :model-value="searchValue"
              @update:model-value="(value) => onUpdateSearchValue(value?.toString())"
            >
              <template #leftInputAddons>
                <div class="tir-pm-dropdown__magnifying-glass-icon">
                  <MagnifyingGlassIcon />
                </div>
              </template>
            </TirPmInput>
          </div>
          <div class="tir-pm-dropdown__option-list" v-if="options.length">
            <TirPmScrollbar
              :style="{ maxHeight: optionListScrollHeight }"
              @on-scroll="emit('onScroll', $event)"
              :showOnHover="showOnHover"
            >
              <div class="tir-pm-dropdown__option-list-inner" @click.stop>
                <div v-for="option in viewOptions" :key="option.Id">
                  <div class="tir-pm-dropdown__option-list-li" @click="onClickOption">
                    <slot name="optionList" :option="option" :search-value="searchValue" />
                  </div>
                </div>
              </div>
            </TirPmScrollbar>
          </div>
          <div class="tir-pm-dropdown__bottom-content">
            <slot name="bottom-content" />
          </div>
        </div>
      </template>
    </TirPmPopover>
    <div
      class="tir-pm-dropdown__hint-massage"
      :class="{
        'tir-pm-dropdown__hint-massage--show': isShowHintMessage && !isOpened,
      }"
    >
      {{ hintMessage }}
    </div>
  </div>
</template>

<script lang="ts" setup generic="T extends ITirPmDropdownOption">
import { computed, defineProps, ref, useSlots, defineEmits, PropType } from 'vue';
import { ChevronDownIcon } from 'tir-style-system/icons/solid';
import { MagnifyingGlassIcon } from 'tir-style-system/icons/outline';
import { TirPmPopover, PopoverPositionEnum, PopoverPositionType } from 'tir-pm-popover';
import { TirPmScrollbar } from 'tir-pm-scrollbar';
import { TirPmDropdownSizeEnum, TirPmDropdownVariantEnum } from './enums';
import { ITirPmDropdownOption } from './interfaces';
import { TirPmInput } from 'tir-pm-input';

const emit = defineEmits<{
  /**Событие открытия поповера */
  (e: 'onOpen'): void;
  /**Событие закрытия поповера */
  (e: 'onClose'): void;
  /**Событие обновление строки поиска */
  (e: 'updateSearch', value: string): void;
  /**Событие скролла */
  (e: 'onScroll', event: Event): void;
}>();

const props = defineProps({
  /**Вариант tir-pm-dropdown */
  variant: {
    type: String as PropType<TirPmDropdownVariantEnum>,
    default: TirPmDropdownVariantEnum.Contained,
  },
  /**Выбранные опции */
  selectedOption: {
    type: Object as PropType<T>,
  },
  /**Опции */
  options: {
    type: Array as PropType<Array<T>>,
    default: [],
  },
  /**Высота скорлла для опций */
  optionListScrollHeight: {
    type: String,
    default: '12rem',
  },
  /**Размер */
  size: {
    type: String as PropType<TirPmDropdownSizeEnum>,
    default: TirPmDropdownSizeEnum.Regular,
  },
  /**Лейбл */
  label: {
    type: String,
    default: 'Label',
  },
  /**Плейсхолдер */
  placeholder: {
    type: String,
  },
  /**Дизейбл */
  isDisabled: {
    type: Boolean,
    default: false,
  },
  showOnHover: {
    type: Boolean,
    default: false,
  },
  /**Состояние ошибки */
  isWithError: {
    type: Boolean,
    default: false,
  },
  /**Подсказка */
  hintMessage: {
    type: String,
    default: '',
  },
  /**Показывать ли подсказку */
  isShowHintMessage: {
    type: Boolean,
    default: false,
  },
  /**Классы для поповера */
  popoverClasses: {
    type: String,
    default: '',
  },
  /**Позиция поповера */
  popoverPosition: {
    type: [String, Object] as PropType<PopoverPositionEnum | PopoverPositionType>,
    default: (): PopoverPositionType => ({
      top: '0.25rem + $el:height',
      left: '0rem',
    }),
  },
  /**Ширина поповера */
  popoverWidth: {
    type: String,
    default: '15rem',
  },
  /**Отключить поповер */
  isDisabledPopover: {
    type: Boolean,
    default: false,
  },
  /**Лейбл для инпута поиска */
  searchInputLabel: {
    type: String,
    default: 'Поиск',
  },
  /**Плейсхолдер для инпута поиска */
  searchInputPlaceholder: {
    type: String,
  },
  /**
   * Включен локальный поиск в массиве.
   * Есть возможность отключить и использовать внешний поиск, например отправляя запросы к апи
   */
  isSearchLocal: {
    type: Boolean,
    default: true,
  },
  /**Скрыть поиск */
  isHiddenSearch: {
    type: Boolean,
    default: false,
  },
  /**Закрыть после выбора */
  closeAfterSelect: {
    type: Boolean,
    default: true,
  },
  /**Ширина */
  width: {
    type: String,
    default: '12.5rem',
  },
  /** показывать выбранный в общем списке */
  isExcludeSelected: {
    type: Boolean,
    default: true,
  },
});

/**Слоты */
const slots = useSlots();
/**Контент выбран */
const isSelectedContent = computed(() => slots && slots.selectedContent && slots.selectedContent());
/**Есть ли сло слева */
const isLeftSlot = computed(() => slots && slots.left && slots.left());
/**Есть ли слот справа */
const isRightSlot = computed(() => slots && slots.right && slots.right());

/**Ссылка на элемент поповера */
const $popover = ref<InstanceType<typeof TirPmPopover>>();
/**Открыт ли поповер */
const isOpened = ref<boolean>(false);
/**При открытии поповера */
const onOpenPopover = () => {
  isOpened.value = true;
  emit('onOpen');
};
/**При закрытии поповера */
const onClosePopover = () => {
  isOpened.value = false;
  searchValue.value = '';
  emit('onClose');
};
/**Открыть поповер */
const openPopover = () => {
  $popover.value?.open();
};
/**Закрыть поповер */
const closePopover = () => {
  $popover.value?.close();
};

/**Включен ли поиск */
const isEnabledSearch = computed(
  () =>
    (!props.isSearchLocal || props.options.some((option) => option.search)) &&
    !props.isHiddenSearch,
);
/**Строка поиска */
const searchValue = ref('');
/**Функция обновления строки поиска */
const onUpdateSearchValue = (value: string) => {
  searchValue.value = value;
  emit('updateSearch', value);
};

/**Видимые опции */
const viewOptions = computed(() => {
  let options: T[];
  if (props.isExcludeSelected) {
    options = props.options.filter((option) => !getIsSelectedOption(option));
  } else {
    options = props.options;
  }
  if (props.isSearchLocal && isEnabledSearch.value)
    options = options.filter((option) => !option.search || option.search(searchValue.value));

  return options;
});

/**Проверить выбранна ли опция */
const getIsSelectedOption = (option: T) =>
  props.selectedOption && option.Id === (props.selectedOption as T).Id;

/**При клике на опцию */
const onClickOption = () => {
  if (props.closeAfterSelect) closePopover();
};

defineExpose({
  open: openPopover,
  close: closePopover,
  searchValue,
});
</script>

<style lang="scss">
.tir-pm-dropdown {
  .tir-popover_element {
    width: 100%;
  }

  &__magnifying-glass-icon {
    width: 1.5rem;
    height: 1.5rem;

    transition: color 0.2s ease;
  }

  &__search-input {
    &.input--focused {
      .tir-pm-dropdown__magnifying-glass-icon {
        color: var(--neutral-50, #93969b);
      }
    }
  }
}
</style>
<style lang="scss" scoped>
@use 'sass:map';

@import 'tir-style-system/expose-variables';
@import 'tir-style-system/expose-default-theme';
@import 'tir-style-system/core/mixins';

.tir-pm-dropdown {
  position: relative;
  width: fit-content;

  &__area {
    display: flex;
    border-radius: var(--8, map.get($margins, '8'));
    border: 0.125rem solid transparent;
    align-items: start;
    min-width: 17.5rem;
    cursor: pointer;
    box-sizing: border-box;
    width: 100%;
    background-color: var(--neutral-10, map.get($colors, 'neutral-10'));
    transition: border 0.2s ease;
    padding-right: 0.625rem;
    outline: none;

    &-variant--detached {
      border: none !important;
      padding-right: unset;
    }
    &.tir-pm-dropdown--light {
      background-color: var(--neutral-0, #ffffff);
      border: 0.063rem solid var(--neutral-80, #52575f);
      border-radius: var(--6, map.get($margins, '6'));
      min-width: auto;

      .tir-pm-dropdown__placeholder {
        font-size: 0.875rem;
      }
    }
    &-block {
      display: flex;
      align-items: center;
      &:hover {
        .tir-pm-dropdown__icon-open-variant--detached {
          background-color: var(--neutral-translucent-5);
        }
      }
      &:active {
        .tir-pm-dropdown__icon-open-variant--detached {
          background-color: var(--neutral-translucent-10);
        }
      }
    }
  }
  &--with-hint {
    margin-bottom: 1.25rem;
  }
  &--reduced &__area {
    min-height: 3rem;
  }
  &--reduced &__desc-select {
    min-height: 2.75rem;
  }
  &--opened &__area {
    &:not(.tir-pm-dropdown--light) {
      background-color: var(--neutral-00, map.get($colors, 'neutral-00'));
      border: 0.125rem solid var(--neutral-40, map.get($colors, 'neutral-40'));
    }
  }

  &__desc-select {
    flex-grow: 1;
    min-height: 3.25rem;
    padding-top: 0.125rem;
    padding-bottom: 0.125rem;
    margin-right: 0.25rem;
    display: flex;
    justify-content: center;
    flex-direction: column;
    min-width: 0;

    &.tir-pm-dropdown--light {
      min-height: 2rem;
    }
  }

  &__label {
    @include set-line-text-size(1);
    color: var(--text-secondary, map.get($colors, 'text-secondary'));
    user-select: none;
    transition: font-size 0.2s ease;

    &.light {
      font-size: 0.75rem;
      line-height: 1rem;
    }
  }

  &__selected-item {
    @include set-line-text-size(1);
    color: var(--text-tertiary, map.get($colors, 'text-tertiary'));
    white-space: nowrap;
    overflow-x: hidden;
    text-overflow: ellipsis;
    user-select: none;
    transition: height 0.2s ease, opacity 0.2s ease;
    opacity: 1 !important;
    height: fit-content !important;
  }
  &__selected-item--show {
    height: fit-content;
    opacity: 1;
  }

  &__icon-open {
    display: flex;
    padding-top: 0.625rem;
    svg {
      margin: auto;
      width: 1.5rem;
      height: 1.5rem;
      transition: transform 0.2s ease;
      color: var(--neutral-90, map.get($colors, 'neutral-90'));
    }
    &-variant--detached {
      padding: 0.25rem !important;
      border-radius: 0.25rem;
      cursor: pointer;
    }
    &.tir-pm-dropdown--light {
      padding-top: 0.5rem;

      svg {
        width: 1rem;
        height: 1rem;
      }
    }
  }

  &--opened &__label,
  &--selected &__label {
    @include set-line-text-size(0.875);
    &.light {
      font-size: 0.75rem;
    }
  }
  &--opened &__icon-open {
    svg {
      transform: rotate(180deg);
    }
  }
  &--selected &__selected-item {
    color: var(--text-primary, map.get($colors, 'text-primary'));
  }

  &__area-right-slot,
  &__area-left-slot {
    display: flex;
    gap: 0.25rem;
    align-items: center;
  }
  &__area-right-slot {
    padding-right: 0.25rem;
    padding-top: 0.626rem;
  }
  &__area-left-slot {
    padding-top: 0.626rem;
    padding-left: 0.626rem;
  }

  &--disabled {
  }
  &__placeholder {
    color: var(--text-secondary, map.get($colors, 'text-secondary'));
  }
  &__hint-massage {
    visibility: hidden;
    color: var(--text-secondary, map.get($colors, 'text-secondary'));
    @include set-line-text-size(0.75);
    user-select: none;
    position: absolute;
    bottom: -1.25rem;
    left: 0;
  }
  &__hint-massage--show {
    visibility: visible;
  }
  &--error &__hint-massage {
    color: var(--text-negative, map.get($colors, 'text-negative'));
  }
  &--error &__area {
    border: 0.125rem solid var(--status-negative, map.get($colors, 'status-negative'));
  }

  &__option-list-li {
    cursor: pointer;

    &:hover {
      background-color: var(--neutral-translucent-5, map.get($colors, 'neutral-translucent-5'));
    }
    &:active {
      background-color: var(--neutral-translucent-10, map.get($colors, 'neutral-translucent-10'));
    }
  }
  &__search-input-section {
    padding: 0.75rem;
    &:not(.is-empty-list) {
      border-bottom: 0.063rem solid var(--neutral-10, #eaeaeb);
    }
  }
  &__selected-list-inner {
    padding: 0 0.75rem;
  }
  &__popover-content {
    border-radius: var(--tir-popover-br);
    overflow: hidden;
    border: 0.063rem solid var(--neutral-10, #eaeaeb);
  }
  &__target {
    display: flex;
    flex-direction: column;
    row-gap: 0.25rem;
  }
  &__bottom-content {
    &:empty {
      display: none;
    }
  }
}
</style>
